import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signOut as amplifySignOut,
} from '@aws-amplify/auth'
import { isAxiosError } from 'axios'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { Outlet, useLocation, useNavigate } from 'react-router'
import styled from 'styled-components'
import { PopoverModal } from 'sutra/components/PopoverModal/PopoverModal'

import { handleException, runOperation } from 'clients/exception-handling'
import { useSessionManager, useTokenManager } from 'clients/yogi-api'
import { useRegisterBootstrapLoading } from 'components'
import { AuthContext } from 'features/auth'
import { ViewAsRoleProvider } from 'features/auth/context'
import { isAuthErrorNamed } from 'features/auth/expected-auth-error'
import {
  NEW_PASSWORD,
  REQUEST_ACCESS,
  SETUP_PASSWORD,
  SIGN_IN,
  SIGN_IN_CALLBACK,
  TRIAL_SIGNUP,
} from 'features/auth/paths'
import { useAuthService } from 'features/auth/service'
import {
  AuthContextInterface,
  User,
  UserDetailsResponse,
} from 'features/auth/types'
import { useProjectStore } from 'features/project/store'
import { identifyChameleon } from 'trackers/chameleon'
import { loginCrisp } from 'trackers/crisp'
import mixpanel from 'trackers/mixpanel'
import { useSentryUser } from 'trackers/sentry'
import { Role } from 'types/hub-role'
import type { ProjectState } from 'types/project'

export const AuthContainer: React.FC<{
  children?: React.ReactNode
}> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState<boolean>(true)
  const [user, setUser] = useState<AuthContextInterface['user']>(null)
  const [userDetails, setUserDetails] = useState<UserDetailsResponse | null>(
    null,
  )
  const [authenticated, setAuthenticated] = useState(false)
  const [username, updateUsername] =
    useState<AuthContextInterface['username']>(null)

  const queryClient = useQueryClient()
  const tokenManager = useTokenManager()
  const sessionManager = useSessionManager()
  const authService = useAuthService()

  // Active hub uuid — the per-hub `hub_role` is resolved for this hub and
  // refetched whenever it changes so the value can't go stale across switches.
  const activeHubUuid = useProjectStore(
    (s: ProjectState) => s.details?.proj_uuid ?? '',
  )

  // Refetch the RBAC v2 roles for the active hub and merge them into
  // `userDetails`. Keyed on the hub uuid so a hub switch re-resolves `hub_role`.
  useEffect(() => {
    if (!authenticated || !activeHubUuid) return
    let cancelled = false
    authService
      .fetchUserContext(activeHubUuid)
      .then(({ hub_role }) => {
        if (cancelled) return
        setUserDetails((prev) => (prev ? { ...prev, hub_role } : prev))
      })
      .catch((cause) => {
        handleException(cause, { operation: 'auth.hub-role.load' })
        // Best-effort: leave the fail-closed defaults in place on error.
      })
    return () => {
      cancelled = true
    }
  }, [authenticated, activeHubUuid, authService])

  useSentryUser(
    userDetails
      ? { email: userDetails.email.toLowerCase(), org: userDetails.org }
      : null,
  )

  useEffect(() => {
    if (userDetails) {
      const { email, first_name, last_name } = userDetails
      const emailLower = email?.toLowerCase()
      const fullName = [first_name, last_name].filter(Boolean).join(' ').trim()
      void runOperation(
        () => {
          loginCrisp(emailLower ?? '', first_name, last_name)
          mixpanel.identify(emailLower)
          mixpanel.track('login', { date: new Date().toISOString() })
          identifyChameleon(emailLower ?? '', {
            email: emailLower,
            name: fullName || undefined,
          })
          window.clarity?.('set', 'email', emailLower)
        },
        {
          operation: 'auth.analytics.identify',
          recover: {
            reason: 'Authentication continues without optional analytics.',
            run: () => undefined,
          },
        },
      )
    }
  }, [userDetails])

  const signIn = useCallback(
    async (user: User) => {
      const attributes = await fetchUserAttributes()
      const userWithAttributes = { ...user, attributes }
      const { tokens } = await fetchAuthSession()
      const token = tokens?.idToken?.toString()
      if (token) {
        tokenManager.setToken(token)
        sessionManager.resetExpired()
      }
      setUser(userWithAttributes as User)
      setAuthenticated(true)
      void runOperation(
        () => {
          const email = attributes?.email?.toLowerCase()
          mixpanel.identify(email)
          mixpanel.set({
            $email: email,
            $name: attributes?.given_name + ' ' + attributes?.family_name,
            USER_ID: email,
            cognito_id: attributes?.sub,
          })
          window.clarity?.('set', 'email', email)
        },
        {
          operation: 'auth.analytics.profile',
          recover: {
            reason: 'Authentication continues without optional analytics.',
            run: () => undefined,
          },
        },
      )

      try {
        const currentUserDetailsResponse = await authService.fetchUser()
        // The legacy `/user` surface has no RBAC v2 hub role. Spread its
        // (legacy) payload, then apply a `guest` placeholder; the hub-switch
        // effect above fetches the real per-hub `hub_role` (which the server
        // defaults to `member` when unresolved) once a hub is active.
        const legacyDetails = currentUserDetailsResponse.data as Omit<
          UserDetailsResponse,
          'hub_role'
        >
        setUserDetails({
          ...legacyDetails,
          hub_role: Role.Guest,
        })
      } catch (e: unknown) {
        if (isAxiosError(e) && e.response?.status === 404) {
          handleException(e, {
            operation: 'auth.profile.load',
            disposition: 'expected',
            isExpectedError: (cause) =>
              isAxiosError(cause) && cause.response?.status === 404,
            reason: 'Authenticated account requires application access.',
          })
          navigate(REQUEST_ACCESS)
          return
        }
        throw e
      }
    },
    [tokenManager, sessionManager, authService, navigate],
  )

  const signOut = useCallback(async () => {
    sessionManager.markExpired()
    await runOperation(() => amplifySignOut(), {
      operation: 'auth.sign-out',
      cleanup: () => {
        setAuthenticated(false)
        setUser(null)
        tokenManager.clearToken()
        queryClient.clear()
        navigate(SIGN_IN)
        void runOperation(() => mixpanel.reset(), {
          operation: 'auth.analytics.reset',
          recover: {
            reason: 'Local sign-out completes without optional analytics.',
            run: () => undefined,
          },
        })
      },
    })
  }, [sessionManager, tokenManager, navigate, queryClient])

  // --- Session expiry modal ---
  const [sessionExpired, setSessionExpired] = useState(false)
  const sessionRedirectRef = useRef('')

  // Provide a ref so the session-expired handler always uses fresh location
  const locationRef = useRef(location)
  locationRef.current = location

  // Register a handler so the axios interceptor can trigger a clean sign-out
  // when token refresh fails, instead of showing a confusing error modal.
  useEffect(() => {
    sessionManager.registerExpiredHandler(() => {
      const currentPath =
        locationRef.current.pathname + locationRef.current.search
      const alreadyOnSignIn = locationRef.current.pathname === SIGN_IN

      // Only show "Session Expired" when the user was actually in the app and
      // their session expired. On a fresh sign-in page, a 401 just means not
      // logged in — don't show the modal.
      if (alreadyOnSignIn) {
        tokenManager.clearToken()
        sessionManager.resetExpired()
        return
      }

      sessionRedirectRef.current =
        currentPath && currentPath !== '/' && currentPath !== '/sign-in'
          ? `?redirect=${encodeURIComponent(currentPath)}`
          : ''

      // Don't tear down React auth state here — that would cause the app to
      // re-render as "logged out" behind the modal (showing the access-denied
      // page, etc.). Just strip the token so no more API calls succeed, and
      // show the modal. Full cleanup happens when the user clicks "Sign in".
      tokenManager.clearToken()

      setSessionExpired(true)
    })
    return () => sessionManager.unregisterExpiredHandler()
  }, [sessionManager, tokenManager])

  const handleSessionExpiredLogin = useCallback(async () => {
    try {
      await amplifySignOut()
    } catch (cause) {
      handleException(cause, {
        operation: 'auth.expired-session.sign-out',
        disposition: 'recovered',
        reason:
          'Expired credentials are cleared and full-page sign-in follows.',
      })
      // Best-effort
    }

    // Full page redirect so all JS module state (including _sessionExpiredFired)
    // is wiped clean. Client-side navigate() would keep the stale flag, causing
    // API calls to be aborted after re-authentication.
    window.location.href = `${SIGN_IN}${sessionRedirectRef.current}`
  }, [])

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      try {
        setLoading(true)
        // if user is already authenticated, construct their user obj
        const user = await getCurrentUser()

        // Ensure auth initialization completes (including setting the token)
        // before rendering children that might fire API requests.
        await signIn(user as User)
        updateUsername(null)
      } catch (cause) {
        handleException(cause, {
          operation: 'auth.initialize',
          disposition: 'expected',
          isExpectedError: (error) =>
            isAuthErrorNamed(
              error,
              'UserUnAuthenticatedException',
              'NotAuthorizedException',
            ),
          reason: 'Unauthenticated visitors are directed to sign-in.',
        })
        const currentPath = locationRef.current.pathname
        const currentSearch = locationRef.current.search
        if ([TRIAL_SIGNUP].includes(currentPath)) {
          navigate(TRIAL_SIGNUP)
        } else if (
          !tokenManager.hasSharedToken() &&
          ![SETUP_PASSWORD, NEW_PASSWORD].includes(currentPath) &&
          ![SETUP_PASSWORD, NEW_PASSWORD, SIGN_IN_CALLBACK].includes(
            currentPath,
          )
        ) {
          if (currentPath === SIGN_IN) {
            // Already on sign-in (e.g. after session-expiry redirect).
            // Don't navigate — it would strip the ?redirect= query param.
          } else if (currentPath && currentPath !== '/') {
            navigate(
              `${SIGN_IN}?redirect=${encodeURIComponent(currentPath + currentSearch)}`,
            )
          } else {
            navigate(`${SIGN_IN}`)
          }
        }
      } finally {
        setLoading(false)
      }
    }

    checkAuth()
    // Auth check runs once on mount. Route-dependent values are read from
    // locationRef so navigation within the app doesn't re-trigger this.
  }, [signIn, tokenManager, navigate, updateUsername])

  // Register auth loading state into the global bootstrap overlay. Must be
  // called before any early return (hooks rule). The overlay in App.tsx stays
  // visible as long as this or any other gate is active — no re-mount glitch.
  useRegisterBootstrapLoading('auth', loading)

  if (loading) {
    return null
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        username,
        updateUsername,
        userDetails,
        isAuthenticated: authenticated,
        signIn,
        signOut,
        setUser,
      }}
    >
      <ViewAsRoleProvider>{children || <Outlet />}</ViewAsRoleProvider>
      <PopoverModal
        isOpen={sessionExpired}
        onClose={() => undefined}
        showClose={false}
        disablePointerDismissal
        disableEscapeDismissal
        footer={null}
        width={360}
        content={
          <SessionExpiredBody>
            <h3 style={{ margin: '0 0 8px' }}>Session Expired</h3>
            <p style={{ margin: '0 0 20px', color: '#555', fontSize: 14 }}>
              Please sign back in to continue.
            </p>
            <button
              onClick={handleSessionExpiredLogin}
              style={{
                width: '100%',
                padding: '8px 0',
                fontSize: 14,
                fontWeight: 600,
                color: '#fff',
                backgroundColor: '#5072A4',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Sign in
            </button>
          </SessionExpiredBody>
        }
      />
    </AuthContext.Provider>
  )
}

const SessionExpiredBody = styled.div`
  text-align: center;
`
